/**
 * Generic content list screen.
 *
 * Every workflow-controlled content type uses this one screen, parameterised by the `:resource`
 * route segment. Bulk actions that require the publish capability are disabled — not hidden — for
 * roles that lack it, so the user understands why they cannot use them.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, ButtonLink } from '@/components/ui';
import { DataTable, type BulkAction, type Column } from '@/components/admin/DataTable';
import { LoadError } from '@/components/site/blocks';
import { useAuth } from '@/context/AuthContext';
import { useDebounced } from '@/hooks';
import { CMS_RESOURCES, cmsApi, type CmsRecord, type CmsResourceKey } from '@/services/api';
import { ApiError } from '@/services/client';

const STATUS_TONES: Record<string, 'success' | 'signal' | 'primary' | 'neutral'> = {
  published: 'success',
  review: 'signal',
  approved: 'primary',
  draft: 'neutral',
  archived: 'neutral',
};

export function ContentListPage() {
  const { resource = '' } = useParams();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'warning' | 'danger'; message: string } | null>(null);

  const config = CMS_RESOURCES[resource as CmsResourceKey];
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') ?? '');
  const debouncedSearch = useDebounced(searchTerm, 300);

  const status = searchParams.get('status') ?? '';
  const needsReview = searchParams.get('needs_review') ?? '';
  const ordering = searchParams.get('ordering') ?? '-updated_at';
  const page = Number(searchParams.get('page') ?? 1);

  const listQuery = useQuery({
    queryKey: ['cms', resource, { debouncedSearch, status, needsReview, ordering, page }],
    queryFn: () =>
      cmsApi.list(resource, {
        search: debouncedSearch || undefined,
        status: status || undefined,
        needs_review: needsReview || undefined,
        ordering,
        page,
        page_size: 20,
      }),
    enabled: Boolean(config),
  });

  const bulkMutation = useMutation({
    mutationFn: ({ action, ids }: { action: string; ids: number[] }) => cmsApi.bulk(resource, ids, action),
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cms', resource] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'dashboard'] });
      if (result.skipped.length > 0) {
        setFeedback({
          tone: 'warning',
          message: `${result.updated.length} updated. ${result.skipped.length} skipped: ${result.skipped[0]?.detail ?? ''}`,
        });
      } else {
        setFeedback({ tone: 'success', message: `${result.updated.length} record(s) ${variables.action}ed.` });
      }
    },
    onError: (error) => {
      setFeedback({
        tone: 'danger',
        message: error instanceof ApiError ? error.message : 'The bulk action failed.',
      });
    },
  });

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  }

  const columns = useMemo<Column<CmsRecord>[]>(() => {
    const titleField = config?.titleField ?? 'title';
    return [
      {
        key: titleField,
        header: 'Title',
        sortable: true,
        render: (row) => (
          <div className="min-w-0">
            <Link to={`/admin/${resource}/${row.slug}`} className="block truncate font-medium no-underline hover:underline">
              {String(row[titleField] ?? row.slug)}
            </Link>
            <p className="mt-0.5 truncate text-xs text-muted">/{row.slug}</p>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        width: '9rem',
        render: (row) => (
          <div className="flex items-center gap-1.5">
            <Badge tone={STATUS_TONES[row.status] ?? 'neutral'}>{row.status}</Badge>
            {row.needs_review && <Badge tone="warning">Needs input</Badge>}
          </div>
        ),
      },
      {
        key: 'is_featured',
        header: 'Featured',
        width: '6rem',
        render: (row) => (row.is_featured ? <span aria-label="Featured">★</span> : <span className="text-muted">—</span>),
      },
      {
        key: 'updated_at',
        header: 'Updated',
        sortable: true,
        width: '9rem',
        render: (row) => (
          <span className="text-muted">
            {new Date(row.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        align: 'right',
        width: '7rem',
        render: (row) => (
          <div className="flex justify-end gap-3">
            <Link to={`/admin/${resource}/${row.slug}`} className="text-sm font-medium no-underline hover:underline">
              Edit
            </Link>
            {row.status === 'published' && (
              <a
                href={`/${resource === 'case-studies' ? 'case-studies' : resource}/${row.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-muted no-underline hover:underline"
              >
                View
              </a>
            )}
          </div>
        ),
      },
    ];
  }, [config?.titleField, resource]);

  if (!config) {
    return (
      <Alert tone="danger" title="Unknown content type">
        <p>
          There is no CMS screen for “{resource}”. Pick a content type from the sidebar.
        </p>
      </Alert>
    );
  }

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  const publishDisabled = !can('can_publish');
  const bulkActions: BulkAction[] = [
    { value: 'submit', label: 'Submit for review' },
    { value: 'approve', label: 'Approve', disabled: publishDisabled },
    { value: 'publish', label: 'Publish', disabled: publishDisabled },
    { value: 'unpublish', label: 'Unpublish', disabled: publishDisabled },
    { value: 'archive', label: 'Archive', disabled: publishDisabled },
    { value: 'feature', label: 'Mark as featured' },
    { value: 'unfeature', label: 'Remove featured' },
    ...(can('can_delete_content') ? [{ value: 'delete', label: 'Delete permanently', destructive: true }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{config.label}</h1>
          <p className="mt-1 text-sm text-muted">Create, review and publish {config.label.toLowerCase()}.</p>
        </div>
        <ButtonLink to={`/admin/${resource}/new`}>New {config.label.replace(/s$/, '').toLowerCase()}</ButtonLink>
      </div>

      {feedback && (
        <Alert tone={feedback.tone} title={feedback.tone === 'success' ? 'Done' : 'Partly applied'}>
          {feedback.message}
        </Alert>
      )}

      {publishDisabled && (
        <Alert tone="info">
          Your role can create and edit content and submit it for review. Publishing is done by an Editor or Content
          Admin.
        </Alert>
      )}

      <DataTable
        caption={`${config.label} list`}
        columns={columns}
        rows={listQuery.data?.results ?? []}
        count={listQuery.data?.count ?? 0}
        page={listQuery.data?.page ?? 1}
        pages={listQuery.data?.pages ?? 1}
        isLoading={listQuery.isLoading}
        search={searchTerm}
        searchPlaceholder={`Search ${config.label.toLowerCase()}…`}
        ordering={ordering}
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { value: '', label: 'All statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'review', label: 'In review' },
              { value: 'approved', label: 'Approved' },
              { value: 'published', label: 'Published' },
              { value: 'archived', label: 'Archived' },
            ],
          },
          {
            key: 'needs_review',
            label: 'Placeholder data',
            options: [
              { value: '', label: 'Any' },
              { value: 'true', label: 'Needs input' },
              { value: 'false', label: 'Complete' },
            ],
          },
        ]}
        filterValues={{ status, needs_review: needsReview }}
        bulkActions={bulkActions}
        emptyTitle={`No ${config.label.toLowerCase()} match this view`}
        emptyDescription="Adjust the filters, or create a new record."
        onSearch={(value) => {
          setSearchTerm(value);
          setParam('search', value);
        }}
        onFilter={setParam}
        onSort={(key) => setParam('ordering', ordering === key ? `-${key}` : key)}
        onPage={(next) => setParam('page', next === 1 ? '' : String(next))}
        onBulkAction={async (action, ids) => {
          await bulkMutation.mutateAsync({ action, ids });
        }}
      />
    </div>
  );
}
